'use client';

import {
  ArrowRight,
  ChevronsUpDown,
  LogIn,
  Plus,
  Rocket,
  Settings,
  UsersRoundIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { MEDIA_URL } from '@/config';
import { CreateTeam } from '@/features/teams/components/create-team';
import { JoinTeam } from '@/features/teams/components/join-team';
import { TeamSettingsDialog } from '@/features/teams/components/team-settings/team-settings';
import { useDefaultTeam } from '@/features/users/api/getDefaultTeam';
import { useUserTeams } from '@/features/users/api/getUserTeams';
import { useUpdateUser } from '@/features/users/api/updateUser';
import { useSession } from '@/hooks/useSession';
import { Team } from '@/types';

export const TeamSwitcher = () => {
  const { isLoggedIn, isLoading: isSessionLoading, session } = useSession();
  const [isCreateTeamOpen, setCreateTeamOpen] = useState(false);
  const [isJoinTeamOpen, setJoinTeamOpen] = useState(false);
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [activeSettingsView, setActiveSettingsView] =
    useState<string>('team-info'); // Default view

  const {
    data: defaultTeam,
    isLoading: isLoadingDefault,
    error: defaultError,
  } = useDefaultTeam({
    queryConfig: {
      enabled: isLoggedIn,
    },
  });

  const {
    data: teams,
    isLoading: isLoadingTeams,
    error: teamsError,
  } = useUserTeams({
    queryConfig: {
      enabled: isLoggedIn,
    },
  });

  const updateUserMutation = useUpdateUser();

  const [activeTeam, setActiveTeam] = useState<Team | null>(null);

  useEffect(() => {
    if (defaultTeam !== undefined) {
      if (!defaultTeam) {
        setSettingsOpen(false);
      }
      setActiveTeam(defaultTeam);
    }
  }, [defaultTeam]);

  const handleTeamSelect = (team: Team) => {
    setActiveTeam(team);
    updateUserMutation.mutate({ data: { defaultTeam: team.id } });
  };

  const openSettings = (view: string) => {
    setActiveSettingsView(view);
    setSettingsOpen(true);
  };

  if (isLoadingDefault || isLoadingTeams || isSessionLoading) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" disabled>
            <Skeleton className="ronded-lg size-8" />
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">Loading...</span>
              <span className="truncate text-xs">Fetching teams</span>
            </div>
            <ChevronsUpDown className="ml-auto" />
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (defaultError || teamsError) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" className="cursor-not-allowed">
            <Skeleton className="ronded-lg size-8" />
            <div className="grid flex-1 text-left text-sm leading-tight text-red-500">
              <span className="truncate font-semibold">Error</span>
              <span className="truncate text-xs">
                {defaultError?.message ||
                  teamsError?.message ||
                  'Failed to load teams'}
              </span>
            </div>
            <ChevronsUpDown className="ml-auto text-muted-foreground" />
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  if (!isLoggedIn) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg">
            <Avatar className="size-8">
              <AvatarFallback className="rounded-lg">
                <UsersRoundIcon
                  size={16}
                  className="opacity-60"
                  aria-hidden="true"
                />
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">No Team</span>
              <span className="truncate text-xs">Login to select a team</span>
            </div>
            <LogIn className="ml-auto text-muted-foreground" />
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              {!activeTeam ? (
                <>
                  <Avatar className="size-8">
                    <AvatarFallback className="rounded-lg">
                      <UsersRoundIcon
                        size={16}
                        className="opacity-60"
                        aria-hidden="true"
                      />
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">No Team</span>
                    <span className="truncate text-xs">Select a team</span>
                  </div>
                  <ChevronsUpDown className="ml-auto text-muted-foreground" />
                </>
              ) : (
                <>
                  <Avatar className="size-8">
                    <AvatarImage
                      src={`${MEDIA_URL}/images/uploads/${activeTeam.team_photo}`}
                      alt={activeTeam.team_name}
                      className="rounded-lg"
                    />
                    <AvatarFallback className="rounded-lg">
                      <UsersRoundIcon
                        size={16}
                        className="opacity-60"
                        aria-hidden="true"
                      />
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {activeTeam.team_name}
                    </span>
                    <span className="truncate text-xs">
                      {activeTeam.isPremium ? 'Subcription' : 'Free'}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto text-muted-foreground" />
                </>
              )}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={'bottom'}
            sideOffset={4}
          >
            {activeTeam && (
              <>
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="size-8">
                      <AvatarImage
                        src={`${MEDIA_URL}/images/uploads/${activeTeam.team_photo}`}
                        alt={activeTeam.team_name}
                        className="rounded-lg"
                      />
                      <AvatarFallback className="rounded-lg">
                        <UsersRoundIcon
                          size={16}
                          className="opacity-60"
                          aria-hidden="true"
                        />
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {activeTeam.team_name}
                      </span>
                      <span className="truncate text-xs">
                        {activeTeam.isPremium ? 'Subcription' : 'Free'}
                      </span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuItem
                  className="cursor-pointer gap-2 p-2"
                  onClick={() => openSettings('team-profile')}
                >
                  <Settings className="size-4 opacity-60" />
                  <div className="font-medium">Settings</div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {teams && (
              <>
                <DropdownMenuLabel className="text-xs uppercase text-muted-foreground">
                  My other teams
                </DropdownMenuLabel>
                {teams
                  .filter((team) => team.id !== activeTeam?.id)
                  .map((team) => (
                    <DropdownMenuItem
                      key={team.id}
                      onClick={() => handleTeamSelect(team)}
                      className="cursor-pointer gap-2 p-2"
                    >
                      <Avatar className="size-6">
                        <AvatarImage
                          src={`${MEDIA_URL}/images/uploads/${team.team_photo}`}
                          alt={team.team_name}
                          className="rounded-sm"
                        />
                        <AvatarFallback className="rounded-sm">
                          <UsersRoundIcon
                            size={16}
                            className="opacity-60"
                            aria-hidden="true"
                          />
                        </AvatarFallback>
                      </Avatar>
                      {team.team_name}
                      <DropdownMenuShortcut className="opacity-100">
                        {team.isPremium && (
                          <Tooltip>
                            <TooltipTrigger>
                              <Rocket
                                size={16}
                                className="text-primary opacity-60"
                              />
                            </TooltipTrigger>
                            <TooltipContent>Active Subscription</TooltipContent>
                          </Tooltip>
                        )}
                      </DropdownMenuShortcut>
                    </DropdownMenuItem>
                  ))}
              </>
            )}
            <DropdownMenuItem
              className="cursor-pointer gap-2 p-2"
              onClick={() => setCreateTeamOpen(true)}
            >
              <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                <Plus className="size-4 opacity-60" />
              </div>
              <div className="font-medium">Create team</div>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="cursor-pointer gap-2 p-2"
              onClick={() => setJoinTeamOpen(true)}
            >
              <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                <ArrowRight className="size-4 opacity-60" />
              </div>
              <div className="font-medium">Join team</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <CreateTeam open={isCreateTeamOpen} setOpen={setCreateTeamOpen} />
        <JoinTeam open={isJoinTeamOpen} setOpen={setJoinTeamOpen} />
        {activeTeam && (
          <TeamSettingsDialog
            open={isSettingsOpen}
            onOpenChange={setSettingsOpen}
            initialView={activeSettingsView}
            activeTeam={activeTeam}
            steamId={session.steamUser.steamid}
          />
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
